import * as React from 'react';
import { FixMeLater } from '../../utils';
import { FieldBase, FieldBaseProps } from '../FieldBase';
import { SelectBase, SelectBaseProps } from '../SelectBase';
import { StylerProps } from '../Styler';

export interface SelectFieldProps
    extends Pick<
            FieldBaseProps,
            | 'fullWidth'
            | 'label'
            | 'shrinkLabel'
            | 'size'
            | 'variant'
            | 'showClear'
            | 'buttonIcon'
            | 'onButtonClick'
            | 'buttonRef'
            | 'width'
            | 'validationStatus'
            | 'icon'
            | 'startDecorator'
            | 'endDecorator'
        >,
        Omit<SelectBaseProps, 'error'> {
    /**
     * A reference passed to the `select` element.
     */
    selectRef?: React.Ref<HTMLSelectElement>;

    /**
     * Additional properties applied to the field's root element.
     */
    FieldRootProps?: StylerProps;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
}

/**
 * SelectFields are components which can be used to allow the user to select an option from a list of given drop down items.
 * It can be created in different sizes with the label configuration to be internal or external label. SelectField also comes in
 * different states to indicate the validity of the selection. There are two core variants to the SelectField default and alternative which can be
 * utilised to fit the need at the time of use.
 */

export function SelectField(props: Readonly<SelectFieldProps>): React.JSX.Element {
    const {
        children,
        disabled,
        fullWidth,
        id,
        label,
        name,
        selectRef,
        size,
        value,
        variant,
        width,
        validationStatus,
        icon,
        startDecorator,
        endDecorator,
        FieldRootProps,
        ref,
        ...other
    } = props;

    return (
        <FieldBase
            ref={ref}
            id={id}
            contains="select"
            disabled={disabled}
            label={label}
            size={size}
            fullWidth={fullWidth}
            variant={variant}
            validationStatus={validationStatus}
            icon={icon}
            startDecorator={startDecorator}
            endDecorator={endDecorator}
            width={width}
            {...FieldRootProps}
        >
            <SelectBase
                ref={selectRef}
                id={id as string}
                name={name}
                value={value}
                disabled={disabled}
                error={validationStatus === 'error'}
                {...other}
            >
                {children}
            </SelectBase>
        </FieldBase>
    );
}
